import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import _ from "lodash";
import moment from "moment";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import CustomPagination from "app/widgets/Shared/Pagination/CustomPagination";
import { Search, SearchIconWrapper, StyledInputBase } from "app/shared/SearchGlobal/style";
import documentServices from "app/services/document-services";
import DocumentFilters from "./DocumentFilters";
import DocumentsSummary from "./DocumentsSummary";
import DeliveryDocumentsDialog from "./DeliveryDocumentsDialog";
import {
    RECEPTION_COLORS,
    RECEPTION_LABELS,
    REGULARIZATION_COLORS,
    REGULARIZATION_LABELS,
} from "./constants";

const PAGE_SIZE = 25;

const EMPTY_FILTERS = {
    documents_status: "",
    date_from: "",
    date_to: "",
    trip_id: "",
};

// Los perfiles que el backend deja entrar (ver ALLOWED_PROFILES en DocumentsController). Se
// repiten acá solo para no mostrar una pantalla que va a contestar 403; la decisión real es del
// backend.
const ALLOWED_PROFILES = ["superadmin", "customer_admin", "supervisor", "planner", "finanzas"];

const DocumentsIndex = () => {
    const navigate = useNavigate();
    const { authUser } = useJumboAuth();

    const [listLoading, setListLoading] = useState(false);
    const [records, setRecords] = useState([]);
    const [headCells, setHeadCells] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [counts, setCounts] = useState(null);
    const [health, setHealth] = useState(null);

    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState(EMPTY_FILTERS);

    const [detailDeliveryId, setDetailDeliveryId] = useState(null);
    const [exporting, setExporting] = useState(false);

    const profileNames = useMemo(
        () => authUser?.profiles?.map((profile) => profile.name) || [],
        [authUser],
    );
    const canViewModule = useMemo(
        () => profileNames.some((name) => ALLOWED_PROFILES.includes(name)),
        [profileNames],
    );

    useEffect(() => {
        if (authUser && !canViewModule) {
            navigate("/trips");
        }
    }, [authUser, canViewModule, navigate]);

    const buildQueryParams = useCallback(() => {
        const params = new URLSearchParams();

        params.set("page", String(currentPage));
        if (searchQuery) params.set("search", searchQuery);

        Object.entries(filters).forEach(([filterName, filterValue]) => {
            if (filterValue !== undefined && filterValue !== null && String(filterValue).trim() !== "") {
                params.set(filterName, String(filterValue).trim());
            }
        });

        return `?${params.toString()}`;
    }, [currentPage, filters, searchQuery]);

    const loadDocuments = useCallback(() => {
        documentServices.getDocuments(buildQueryParams())
            .then((response) => {
                setRecords(response.data || []);
                // Las columnas vienen con los datos, así que agregar una en el backend no
                // necesita tocar esta pantalla.
                if (response.head_cells?.length) setHeadCells(response.head_cells);
                setCurrentPage(response.meta?.current_page || 1);
                setTotalPages(response.meta?.total_pages || 1);
                setTotalCount(response.meta?.total_count || 0);
                setCounts(response.meta?.counts || null);
            })
            .catch((error) => {
                if (error?.response?.status === 403) {
                    navigate("/trips");
                }
            })
            .finally(() => {
                setListLoading(false);
            });
    }, [buildQueryParams, navigate]);

    useEffect(() => {
        setListLoading(true);
    }, []);

    // La salud del modulo no depende de los filtros -habla de toda la empresa-, asi que se pide
    // una sola vez y no en cada recarga de la tabla.
    useEffect(() => {
        documentServices.getHealth()
            .then(setHealth)
            .catch(() => setHealth(null));
    }, []);

    useEffect(() => {
        if (listLoading) {
            loadDocuments();
        }
    }, [listLoading, loadDocuments]);

    const debouncedSearchChange = useMemo(() => _.debounce((nextValue) => {
        setCurrentPage(1);
        setSearchQuery(nextValue);
        setListLoading(true);
    }, 450), []);

    useEffect(() => () => debouncedSearchChange.cancel(), [debouncedSearchChange]);

    const handleSearchChange = (event) => {
        setSearchInput(event.target.value);
        debouncedSearchChange(event.target.value);
    };

    const handleFiltersChange = (nextFilters) => {
        setFilters(nextFilters);
        setCurrentPage(1);
        setListLoading(true);
    };

    const clearFilters = () => {
        setFilters(EMPTY_FILTERS);
        setSearchInput("");
        setSearchQuery("");
        setCurrentPage(1);
        setListLoading(true);
    };

    // Baja los P.O.D. de lo que se está viendo. El nombre del archivo lo manda el backend en el
    // Content-Disposition; si no viene, se arma uno con la fecha.
    const handleExport = () => {
        setExporting(true);
        documentServices.exportPods(buildQueryParams())
            .then((response) => {
                const blob = new Blob([response.data], { type: "application/zip" });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                const fromHeader = /filename="?([^"]+)"?/.exec(response.headers?.["content-disposition"] || "");

                link.href = url;
                link.download = fromHeader ? fromHeader[1] : `pod_${moment().format("YYYY-MM-DD")}.zip`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((error) => {
                // El cuerpo del error también viene como arraybuffer, así que hay que
                // decodificarlo para poder mostrar el motivo real (tope superado, sin resultados).
                let message = "No se pudo generar la descarga.";
                try {
                    const decoded = JSON.parse(new TextDecoder().decode(error?.response?.data));
                    if (decoded?.error) message = decoded.error;
                } catch (parseError) { /* se queda el mensaje genérico */ }

                Swal.fire({ icon: "warning", title: "Descarga no disponible", text: message });
            })
            .finally(() => setExporting(false));
    };

    const formatDate = (value) => (value ? moment(value).format("DD/MM/YYYY") : "-");

    // Cada celda sabe dibujarse sola según el id de la columna que mandó el backend. Es lo que
    // permite que las cabeceras sean dinámicas: una columna nueva sale como texto plano y recién
    // se le da forma acá cuando hace falta.
    const renderCell = (record, cellId) => {
        switch (cellId) {
            case "contentReception":
                return (
                    <Chip
                        size="small"
                        label={RECEPTION_LABELS[record.contentReception] || record.contentReception}
                        color={RECEPTION_COLORS[record.contentReception] || "default"}
                    />
                );
            case "regularization":
                return (
                    <Chip
                        size="small"
                        variant={record.regularization === "not-applicable" ? "outlined" : "filled"}
                        label={REGULARIZATION_LABELS[record.regularization] || record.regularization}
                        color={REGULARIZATION_COLORS[record.regularization] || "default"}
                    />
                );
            case "deliveryDate":
                return formatDate(record.deliveryDate);
            case "state":
                return record.stateName || record.state || "-";
            default:
                return (
                    <Typography variant="body2" noWrap>
                        {record[cellId] || "-"}
                    </Typography>
                );
        }
    };

    const renderTable = () => {
        if (listLoading) {
            return (
                <Stack alignItems="center" sx={{ py: 8 }}>
                    <CircularProgress color="warning" />
                </Stack>
            );
        }

        if (!records.length) {
            return (
                <Card>
                    <CardContent>
                        <Typography variant="body1" color="text.secondary">
                            No hay entregas con los filtros actuales.
                        </Typography>
                    </CardContent>
                </Card>
            );
        }

        return (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, width: "100%" }}>
                <Table size="small" sx={{ width: "100%" }}>
                    <TableHead>
                        <TableRow>
                            {headCells.map((cell) => (
                                <TableCell key={cell.id} align={cell.numeric ? "right" : "left"}>
                                    {cell.label}
                                </TableCell>
                            ))}
                            <TableCell align="right">Ver</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {records.map((record) => (
                            <TableRow
                                key={record.id}
                                hover
                                sx={{ cursor: "pointer" }}
                                onClick={() => setDetailDeliveryId(record.id)}
                            >
                                {headCells.map((cell) => (
                                    <TableCell key={cell.id} align={cell.numeric ? "right" : "left"}>
                                        {renderCell(record, cell.id)}
                                    </TableCell>
                                ))}
                                <TableCell align="right">
                                    <Tooltip title="Ver documentos">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setDetailDeliveryId(record.id);
                                            }}
                                        >
                                            <VisibilityOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    if (authUser && !canViewModule) {
        return null;
    }

    return (
        <React.Fragment>
            <DeliveryDocumentsDialog
                deliveryId={detailDeliveryId}
                onClose={() => setDetailDeliveryId(null)}
                onChanged={() => setListLoading(true)}
            />

            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h2">Documentos</Typography>

                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <Button
                        startIcon={<DownloadOutlinedIcon />}
                        variant="outlined"
                        size="small"
                        disabled={exporting || !counts?.signed}
                        onClick={handleExport}
                    >
                        {exporting ? "Preparando..." : "Descargar P.O.D."}
                    </Button>

                    <Button
                        startIcon={<FilterAltOutlinedIcon />}
                        variant={showFilters ? "contained" : "outlined"}
                        size="small"
                        onClick={() => setShowFilters((previous) => !previous)}
                    >
                        Filtros
                    </Button>

                    <Search sx={{ width: 300 }}>
                        <SearchIconWrapper>
                            <SearchIcon />
                        </SearchIconWrapper>
                        <StyledInputBase
                            placeholder="Entrega, pedido, viaje, folio o cliente..."
                            inputProps={{ "aria-label": "search" }}
                            sx={{ background: "white" }}
                            value={searchInput}
                            onChange={handleSearchChange}
                        />
                    </Search>
                </Stack>
            </Stack>

            <DocumentsSummary
                counts={counts}
                health={health}
                active={filters.documents_status}
                onSelect={(status) => handleFiltersChange({ ...filters, documents_status: status })}
            />

            <DocumentFilters
                open={showFilters}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClearFilters={clearFilters}
            />

            {renderTable()}

            <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={PAGE_SIZE}
                onPageSelect={(page) => {
                    setCurrentPage(page);
                    setListLoading(true);
                }}
            />
        </React.Fragment>
    );
};

export default DocumentsIndex;
